// frontend/src/pages/Mapa.jsx

import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css'; 
import L from 'leaflet';
import '../styles/detalhe.css'; 

const API_BASE_URL = 'https://sigoapi.jhe-etes.com.br/api';

// --- 1. CONFIGURAÇÃO DE CORES DA LEGENDA ---
const STATUS_COLORS = {
  'Operação': '#00AEEF',      // Azul Sabesp
  'Em Obras': '#F59E0B',      // Laranja
  'Planejamento': '#8B5CF6',  // Roxo
  'Licitação': '#10B981',     // Verde
  'Paralisada': '#EF4444'     // Vermelho
};

// --- 2. FUNÇÃO DO ÍCONE (PIN) ---
const createCustomIcon = (color) => {
  const pinColor = color || '#555555';
  
  const svgIcon = `
  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 24 24" fill="${pinColor}" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3" fill="#ffffff"></circle>
  </svg>`;

  return new L.DivIcon({
    className: 'bg-transparent border-none',
    html: svgIcon,
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -38]
  });
};

// --- 3. COMPONENTE DE INPUT (SELECT) ---
const CustomSelect = ({ label, value, onChange, options, defaultText }) => (
  <div className="mb-4">
    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-1">
      {label}
    </label>
    <div className="relative">
      <select 
        value={value} 
        onChange={onChange}
        className="w-full p-3 pl-4 pr-10 bg-white border-2 border-gray-100 text-gray-700 text-sm rounded-xl appearance-none focus:outline-none focus:border-sabesp-cyan/50 focus:ring-2 focus:ring-sabesp-cyan/20 transition-all cursor-pointer shadow-sm hover:border-gray-200"
        style={{ 
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, 
            backgroundPosition: `right 0.75rem center`, 
            backgroundRepeat: `no-repeat`, 
            backgroundSize: `1.5em 1.5em` 
        }}
      >
        <option value="Todos">{defaultText}</option>
        {options.map((opt, idx) => (
            typeof opt === 'object' 
            ? <option key={opt.id} value={opt.nome}>{opt.nome}</option> 
            : <option key={idx} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  </div>
);

function Mapa() {
  const navigate = useNavigate();
  
  // Estados de Dados
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [listaMunicipios, setListaMunicipios] = useState([]); 
  const [listaBacias, setListaBacias] = useState([]); 
  const [listaStatus, setListaStatus] = useState([]); 
  const [loading, setLoading] = useState(true);
  
  // Estados de Filtros
  const [busca, setBusca] = useState(''); // Busca por texto
  const [filtroMunicipio, setFiltroMunicipio] = useState('Todos');
  const [filtroBacia, setFiltroBacia] = useState('Todos');
  const [filtroStatus, setFiltroStatus] = useState('Todos');

  // 1. Carregar Listas de Filtros (Dropdowns) ao iniciar
  useEffect(() => {
    async function carregarListas() {
      try {
        const [respMun, respBacias, respStatus] = await Promise.all([
            fetch(`${API_BASE_URL}/municipios`),
            fetch(`${API_BASE_URL}/bacias`),
            fetch(`${API_BASE_URL}/status`)
        ]);

        if (respMun.ok) setListaMunicipios(await respMun.json());
        if (respBacias.ok) setListaBacias(await respBacias.json());
        if (respStatus.ok) setListaStatus(await respStatus.json());
      } catch (error) {
        console.error("Erro ao carregar listas de filtros:", error);
      }
    }
    carregarListas();
  }, []);

  // 2. Buscar Empreendimentos (Sempre que mudar um filtro ou a busca)
  useEffect(() => {
    // Criamos um timer para não chamar a API a cada letra digitada (Debounce)
    const delayDebounceFn = setTimeout(async () => {
        setLoading(true);
        
        const params = new URLSearchParams();
        
        // Adiciona os parâmetros apenas se tiverem valor
        if (busca.trim()) params.append('busca', busca);
        if (filtroMunicipio !== 'Todos') params.append('municipio', filtroMunicipio);
        if (filtroBacia !== 'Todos') params.append('bacia', filtroBacia);
        if (filtroStatus !== 'Todos') params.append('status', filtroStatus);
        
        try {
            console.log("Buscando na API:", `${API_BASE_URL}/empreendimentos?${params.toString()}`);
            const response = await fetch(`${API_BASE_URL}/empreendimentos?${params.toString()}`);
            
            if (response.ok) {
                const data = await response.json();
                console.log("Dados recebidos:", data);
                setEmpreendimentos(data);
            } else {
                console.error("Erro na resposta da API:", response.status);
            }
        } catch (error) {
            console.error("Erro na busca:", error);
        } finally {
            setLoading(false);
        }
    }, 300); // Espera 300ms após parar de digitar para buscar

    return () => clearTimeout(delayDebounceFn);
  }, [busca, filtroMunicipio, filtroBacia, filtroStatus]);

  // Função para limpar tudo
  const limparFiltros = () => {
      setFiltroMunicipio('Todos');
      setFiltroBacia('Todos');
      setFiltroStatus('Todos');
      setBusca('');
  };

  const centerPosition = [-23.55052, -46.63330]; 

  return (
    <div className="flex h-screen w-full font-sans bg-slate-50 overflow-hidden">
      
      {/* --- SIDEBAR LATERAL --- */}
      <aside className="w-80 h-full flex flex-col z-20 shadow-2xl bg-white/90 backdrop-blur-xl border-r border-white/20">
        
        <div className="p-6 pb-4 bg-gradient-to-r from-blue-50/50 to-transparent">
          <div className="flex items-center gap-2 mb-1">
             <span className="text-2xl">💧</span>
             <h1 className="text-2xl font-extrabold text-sabesp-blue tracking-tight">SIGO Sabesp</h1>
          </div>
          <p className="text-xs text-gray-400 font-medium ml-9 uppercase tracking-wider">Mapa de Empreendimentos</p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            
            {/* CAMPO DE BUSCA */}
            <div className="mb-6 relative">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-1">Pesquisa Rápida</label>
                <div className="relative">
                    <input 
                        type="text" 
                        placeholder="Buscar por nome..." 
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        className="w-full p-3 pl-10 bg-white border-2 border-blue-100 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-sabesp-cyan focus:ring-2 focus:ring-sabesp-cyan/20 transition-all shadow-sm"
                    />
                    <span className="absolute left-3 top-3.5 text-gray-400 text-lg">🔍</span>
                </div>
            </div>

            {/* DROPDOWNS */}
            <div className="space-y-2 mb-8">
                <CustomSelect 
                    label="Município" 
                    value={filtroMunicipio} onChange={(e) => setFiltroMunicipio(e.target.value)} 
                    options={listaMunicipios} defaultText="Todos os municípios" 
                />
                <CustomSelect 
                    label="Bacia Hidrográfica" 
                    value={filtroBacia} onChange={(e) => setFiltroBacia(e.target.value)} 
                    options={listaBacias} defaultText="Todas as bacias" 
                />
                <CustomSelect 
                    label="Situação" 
                    value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} 
                    options={listaStatus} defaultText="Todos os status" 
                />
            </div>

            <button 
                onClick={limparFiltros}
                className="w-full py-3 px-4 bg-blue-50 text-sabesp-blue hover:bg-sabesp-blue hover:text-white rounded-xl text-sm font-bold transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-2 mb-8"
            >
                🧹 Limpar Filtros
            </button>

            {/* LEGENDA */}
            <div className="border-t border-gray-100 pt-6">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 ml-1">Legenda do Mapa</h3>
                <div className="space-y-3 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                    {Object.entries(STATUS_COLORS).map(([name, color]) => (
                        <div key={name} className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full ring-4 ring-opacity-20" style={{ backgroundColor: color, boxShadow: `0 0 0 4px ${color}20` }}></div>
                            <span className="text-sm text-gray-600 font-medium">{name}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        {/* RODAPÉ DA SIDEBAR */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 text-center">
             <p className="text-xs font-medium text-gray-500">
                 Exibindo <span className="text-sabesp-blue font-bold text-base">{empreendimentos.length}</span> obras
             </p>
        </div>
      </aside>

      {/* --- MAPA --- */}
      <main className="flex-grow relative z-0">
        <MapContainer 
            center={centerPosition} 
            zoom={10} 
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
        >
          <TileLayer
            attribution='&copy; CARTO'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          />

          <div className="leaflet-top leaflet-right">
             <div className="leaflet-control-zoom leaflet-bar leaflet-control">
                 <a className="leaflet-control-zoom-in" href="#" title="Zoom in" role="button">+</a>
                 <a className="leaflet-control-zoom-out" href="#" title="Zoom out" role="button">−</a>
             </div>
          </div>
          
          {!loading && empreendimentos.map((emp) => (
            <Marker 
                key={emp.id} 
                position={emp.coordenadas}
                icon={createCustomIcon(emp.corStatus)}
            >
              <Popup className="custom-popup">
                <div className="p-1 min-w-[220px]">
                    <div className="flex justify-between items-start mb-3 pb-2 border-b border-gray-100">
                        <h3 className="font-bold text-sabesp-blue text-sm uppercase leading-tight pr-2">{emp.nome}</h3>
                        <span 
                            className="text-[10px] font-bold text-white px-2 py-0.5 rounded-full whitespace-nowrap shadow-sm"
                            style={{ backgroundColor: emp.corStatus }}
                        >
                            {emp.status}
                        </span>
                    </div>

                    <div className="space-y-2 mb-4 text-xs text-gray-600">
                        <div className="flex items-center gap-2">
                            <span className="text-lg">📍</span> 
                            <span className="font-medium text-gray-800">{emp.municipio}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-lg">💧</span> 
                            <span>Vazão: <strong>{emp.vazaoAtual} L/s</strong></span>
                        </div>
                    </div>

                    <button 
                        onClick={() => navigate(`/empreendimento/${emp.id}`)}
                        className="w-full py-2 bg-sabesp-cyan text-white text-xs font-bold rounded-lg hover:bg-cyan-600 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-1"
                    >
                        Ver Detalhes ➜
                    </button>
                </div>
              </Popup>
            </Marker>
          ))}

        </MapContainer>
      </main>
    </div>
  );
}

// Adicionando estilo da scrollbar
const styles = `.custom-scrollbar::-webkit-scrollbar { width: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; border: 2px solid transparent; background-clip: content-box; } .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }`;
document.head.appendChild(document.createElement("style")).innerHTML = styles;

export default Mapa;